import breedingManagementApi from "@/app/apis/main/breeder/breeding-management-api.js";

export default {
	async getBreederDamAndSireList(queryParams) {
		return breedingManagementApi.getBreederDamAndSireList(queryParams)
	},

	/**
	* Fetches the details of a Dam/Sire by delegating to the DamSireAPI service.
	*
	* @async
	* @function getDamAndSireList
	* @param {number} damSireId - The ID of the Dam/Sire to retrieve.
	* @returns {Promise<Object>} The response from the DamSireAPI service containing the Dam/Sire details.
	* @throws {Error} If the API request fails.
	*/
	async getDamSireDetails(breederSysId, damSireId) {
		return breedingManagementApi.getDamSireDetails(breederSysId, damSireId)
	},

	/**
	* Updates an existing Dam/Sire by delegating to the DamSireAPI service.
	*
	* @async
	* @function updateAdminDamSireOrder Items
	* @param {number} damSireId - The ID of the Dam/Sire to be updated.
	* @param {Object} damSireDetails - The updated details of the Dam/Sire.
	* @param {Object} damSireDetails.formData - The form data for the Dam/Sire.
	* @param {File} [damSireDetails.dogImageUpload] - The file for the dog image (optional).
	* @returns {Promise<Object>} The response from the DamSireAPI service.
	* @throws {Error} If the API request fails.
	*/
	async updateAdminDamSire(breederSysId, damSireId, damSireDetails) {
		return breedingManagementApi.updateAdminDamSire(breederSysId, damSireId, damSireDetails);
	},

	async getMatingList(queryParams) {
		return breedingManagementApi.getMatingList(queryParams)
	},

	async deleteMatingDetails(breederSysId, matingId) {
		return breedingManagementApi.deleteMatingDetails(breederSysId, matingId)
	},

	async getLitterManagementList(queryParams) {
		return breedingManagementApi.getLitterManagementList(queryParams)
	},

	/**
	 * Retrieves a list of breeders based on the specified gender.
	 * 
	 * @param {("Dam" | "Sire")} gender - The gender of the breeders to retrieve. It can be either "Dam" or "Sire".
	 * @returns {Promise<any>} A promise that resolves to a list of breeders matching the specified gender.
	 */
		async getBreederDamAndSireListByGender(breederSysId, gender) {
		return breedingManagementApi.getBreederDamAndSireListByGender(breederSysId, gender);
	},

	/**
	 * Fetches mating details of a Dam/Sire by delegating to the BreedingManagementAPI.
	 *
	 * @async
	 * @function getDamSireDetails
	 * @param {number} matingId - The ID of the mating details to retrieve.
	 * @param {number} breederSysId - Breeder Id of the Breeder
	 * @returns {Promise<Object>} The response from the BreedingManagementAPI service containing the mating details.
	 * @throws {Error} If the API request fails.
	 */
	async getAdminDamAndSireMatingDetails(breederSysId, matingId) {
		return breedingManagementApi.getDamAndSireMatingDetails(breederSysId, matingId)
	},

	/**
	 * Updates mating details for a specific mating ID using the Breeding Management API.
	 * 
	 * @param {number} matingId - The ID of the mating to be updated.
	 * @param {Object} matingDetails - The updated details of the mating.
	 * @param {number | null} matingDetails.damId - The ID of the dam. `null` if not applicable.
	 * @param {number | null} matingDetails.sireId - The ID of the sire. `null` if not applicable.
	 * @param {string} matingDetails.note - Additional notes about the mating.
	 * @returns {Promise<any>} A promise that resolves to the API response after updating the mating details.
	 */
	async updateDamAndSireMatingDetails(breederSysId, matingId, matingDetails) {
		return breedingManagementApi.updateDamAndSireMatingDetails(breederSysId, matingId, matingDetails);
	},

	async getLitterPuppyList(breederSysId, matingId, queryParams) {
		return breedingManagementApi.getLitterPuppyList(breederSysId, matingId, queryParams)
	},

	async deleteLitterPuppy(breederSysId, litterPuppyId) {
		return breedingManagementApi.deleteLitterPuppy(breederSysId, litterPuppyId)
	},

	/**
	 * Fetches ovulation and gestation details for a specific dam.
	 *
	 * @async
	 * @function getDamOvulationAndGestationDetails
	 * @param {string} damId - The ID of the dam.
	 * @param {string} breederSysId - The ID of the breeder system.
	 * @returns {Promise<Object>} A promise that resolves to the ovulation and gestation details.
	 * @throws {Error} Throws an error if the request fails or the response is not as expected.
	 */
	async getDamOvulationAndGestationDetails(damId, breederSysId) {
		return breedingManagementApi.getDamOvulationAndGestationDetails(damId, breederSysId);
	},

	async getDamDiaryDetails(damId, breederSysId) {
		return breedingManagementApi.getDamDiaryDetails(damId, breederSysId);
	},

	/**
	 * Saves ovulation and gestation details for a dam in the Breeding Management system.
	 *
	 * @async
	 * @function saveOvulationAndGestation
	 * @param {Object} details - The ovulation and gestation details to be saved.
	 * @param {number} details.damId - The ID of the dam.
	 * @param {number} details.breederSysId - The ID of the breeder.
	 * @param {string} details.matingDate - The mating date of the dam (in ISO 8601 format).
	 * @param {string} details.ovulationStartDate - The ovulation start date of the dam (in ISO 8601 format).
	 * @returns {Promise<Object>} A promise that resolves to the response from the Breeding Management API.
	 */
	async saveOvulationAndGestation(details) {
		return breedingManagementApi.saveOvulationAndGestation(details);
	},
	
	/**
	 * Fetches the details of the dam and sire litter based on the provided breeder system ID and mating ID.
	 *
	 * @async
	 * @function getDamAndSireLitterDetails
	 * @param {number} breederSysId - The unique identifier for the breeder.
	 * @param {number} litterId - The unique identifier for the litter puppy.
	 * @returns {Promise<Object>} A promise that resolves to the litter details of the dam and sire.
	 * @throws {Error} Throws an error if the API request fails or if the parameters are invalid.
	 */
	async getDamAndSireLitterDetails(breederSysId, litterId) {
		return breedingManagementApi.getDamAndSireLitterDetails(breederSysId, litterId)
	},

	async saveLitterPuppyDetails(breederSysId, litterId, details) {
		return breedingManagementApi.saveLitterPuppyDetails(breederSysId, litterId, details);
	},

	// *********************************** DOCUMENTS ****************************************************
    /**
     * Adds a new document by sending a POST request with form data.
     *
     * @async
     * @function uploadDamSireDocument
     * @param {Object} uploadDetails - The details of the Dam/Sire to be added.
     * @param {Object} uploadDetails.formData - The form data for the Dam/Sire.
     * @param {File} [uploadDetails.document] - The file for the document (required).
     * @returns {Promise<Object>} The response from the API.
     * @throws {Error} If the API request fails.
     */
    async uploadDamSireDocument(breederSysId, uploadDetails) {
        return breedingManagementApi.uploadDamSireDocument(breederSysId, uploadDetails);
    },

    /**
     * Fetches the list of Dam/Sire documents based on the provided query parameters.
     *
     * @async
     * @function getDamSireDocumentsList
     * @param {Object} queryParams - The query parameters used to filter or paginate the document list.
     * @param {string} [queryParams.damSireId] - The ID of the Dam/Sire to filter documents.
     * @param {string} [queryParams.sortBy] - The field to sort by (e.g., 'createdAt').
     * @param {string} [queryParams.filterBy] - The field to sort by (e.g., 'fileName', 'fileType').
     * @param {string} [queryParams.order] - The sort order ('asc' or 'desc').
     * @param {number} [queryParams.page] - The page number for pagination.
     * @param {number} [queryParams.limit] - The number of documents to retrieve per page.
     * @returns {Promise<Object>} The response from the API containing the list of documents.
     * @throws {Error} If the API request fails.
     */
	async getDamSireDocumentsList(damSireId, queryParams) {
        return breedingManagementApi.getDamSireDocumentsList(damSireId, queryParams);
    },

    /**
     * Deletes a dam/sire document from the server.
     * 
     * @async
     * @function deleteDamSireDocument
     * @param {string} documentId - The ID of the document to delete.
     * @param {Object} documentDetails - The details of the document being deleted.
     * @param {string} documentDetails.damSireId - The ID of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the delete operation.
     * @throws {Error} If the API request fails.
     */
    async deleteDamSireDocument(documentId, documentDetails) {
        return breedingManagementApi.deleteDamSireDocument(documentId, documentDetails);
    },

    /**
     * Updates a dam/sire document from the server.
     * 
     * @async
     * @function updateDamSireDocument
     * @param {string} documentId - The ID of the document to update.
     * @param {Object} documentDetails - The details of the document being updated.
     * @param {string} documentDetails.description - The descsription of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the update operation.
     * @throws {Error} If the API request fails.
     */
    async updateDamSireDocument(documentId, documentDetails) {
        return breedingManagementApi.updateDamSireDocument(documentId, documentDetails);
    },

};
